import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SidebarLayout from "./SidebarLayout";
import "./BookingImport.css";

function BookingImport() {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(1); // 1: upload, 2: preview/mapping, 3: results
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [savedMapping, setSavedMapping] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [loadingReadiness, setLoadingReadiness] = useState(true);
  const [supportImports, setSupportImports] = useState([]);
  const navigate = useNavigate();

  // Load saved mapping from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("bookingImportMapping");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSavedMapping(parsed);
      } catch (e) {
      }
    }
  }, []);

  // Check migration readiness on mount
  useEffect(() => {
    checkMigrationReadiness();
  }, []);

  // Support-run imports for this shop, so the owner is told rather than left to notice.
  useEffect(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return;
    const apiBaseUrl =
      process.env.REACT_APP_API_BASE_URL || "";
    axios
      .get(`${apiBaseUrl}/api/support-import/history`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const runs = (response.data?.history || []).filter((row) => row.bySupport);
        setSupportImports(runs);
      })
      .catch(() => setSupportImports([]));
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const checkMigrationReadiness = async () => {
    try {
      setLoadingReadiness(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const apiBaseUrl = getApiBaseUrl();
      const response = await axios.get(
        `${apiBaseUrl}/api/staff/migration-readiness`,
        { headers },
      );
      setReadiness(response.data);
      setLoadingReadiness(false);
    } catch (err) {
      setError(`API Error: ${err.response?.data?.message || err.message}`);
      setLoadingReadiness(false);
    }
  };

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    // Check for .csv extension (MIME type varies by system: text/csv, application/vnd.ms-excel, etc.)
    if (
      selectedFile &&
      (selectedFile.type === "text/csv" || selectedFile.name.endsWith(".csv"))
    ) {
      setFile(selectedFile);
      setError("");
      setStep(1);
      setPreviewData(null);
      setResults(null);
    } else {
      setError("Please select a valid CSV file");
      setFile(null);
    }
  };

  const handlePreview = async () => {
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    try {
      setImporting(true);
      setError("");

      const headers = getAuthHeaders();
      if (!headers) return;

      const apiBaseUrl = getApiBaseUrl();
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${apiBaseUrl}/api/bookings/import/preview`,
        formData,
        {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setPreviewData(response.data);
      // Prefer saved mapping if available, otherwise suggested
      if (savedMapping && Object.keys(savedMapping).length > 0) {
        setColumnMapping(savedMapping);
      } else {
        setColumnMapping(response.data.suggestedMapping || {});
      }
      setStep(2);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to preview file. Please try again.",
      );
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    try {
      setImporting(true);
      setError("");

      const headers = getAuthHeaders();
      if (!headers) return;

      const apiBaseUrl = getApiBaseUrl();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("columnMapping", JSON.stringify(columnMapping));

      const response = await axios.post(
        `${apiBaseUrl}/api/bookings/import`,
        formData,
        {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setResults(response.data);
      setStep(3);
      setFile(null);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to import bookings. Please try again.",
      );
    } finally {
      setImporting(false);
    }
  };

  const handleMappingChange = (ourField, csvColumn) => {
    setColumnMapping((prev) => ({
      ...prev,
      [ourField]: csvColumn,
    }));
  };

  const handleStartOver = () => {
    setStep(1);
    setFile(null);
    setPreviewData(null);
    setColumnMapping({});
    setSavedMapping(savedMapping); // keep saved mapping in memory
    setResults(null);
    setError("");
    if (document.getElementById("file-input")) {
      document.getElementById("file-input").value = "";
    }
  };

  const handleSaveMapping = () => {
    localStorage.setItem("bookingImportMapping", JSON.stringify(columnMapping));
    setSavedMapping(columnMapping);
  };

  const handleClearSavedMapping = () => {
    localStorage.removeItem("bookingImportMapping");
    setSavedMapping(null);
  };

  const downloadTemplate = () => {
    // Generic template that works with mapping
    const template = [
      "Staff Member,Customer Name,Customer Email,Customer Phone,Service,Date,Time,Duration (min),Notes,Status",
      "Mary Smith,John Doe,john@example.com,555-1234,Haircut,2026-02-15,10:00,60,First time customer,confirmed",
      "Mary Smith,Jane Wilson,jane@example.com,555-5678,Color,2026-02-16,14:00,90,,confirmed",
    ].join("\n");

    const blob = new Blob([template], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "booking_import_example.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Field definitions with descriptions
  const fieldDefinitions = {
    staffName: {
      label: "Staff Name*",
      description:
        'Professional who performed the service (e.g., "Mary Smith")',
    },
    staffEmail: {
      label: "Staff Email",
      description: "Optional - use if multiple staff have same name",
    },
    customerName: {
      label: "Customer Name*",
      description: "Customer full name",
    },
    customerEmail: {
      label: "Customer Email*",
      description: "Customer email address",
    },
    customerPhone: {
      label: "Customer Phone",
      description: "Phone number (optional)",
    },
    appointmentDate: {
      label: "Appointment Date*",
      description: "Date in YYYY-MM-DD format (e.g., 2026-01-15)",
    },
    startTime: {
      label: "Start Time*",
      description: "Time in HH:MM format (e.g., 14:30)",
    },
    endTime: {
      label: "End Time",
      description: "Optional if Duration provided",
    },
    duration: {
      label: "Duration (minutes)",
      description: "Used to calculate end time if missing",
    },
    service: {
      label: "Service Name",
      description:
        'Service name (e.g., "Haircut") - will match to your services',
    },
    notes: { label: "Notes", description: "Customer notes (optional)" },
    status: {
      label: "Status",
      description: "confirmed, pending, completed, or cancelled",
    },
  };

  return (
    <SidebarLayout>
      <div className="booking-import-page">
        <div className="import-container">
          <h1>Import Appointments from CSV</h1>
          <p className="subtitle">
            {step === 1 && "Upload your CSV file from any booking platform"}
            {step === 2 && "Review and confirm column mapping"}
            {step === 3 && "Import complete"}
          </p>

          {/* Anything OGESTYLE support imported for this shop is shown here, so the owner sees it without
              having to discover rows they did not add. */}
          {supportImports.length > 0 && (
            <div
              style={{
                border: "1px solid #fde68a",
                background: "#fffbeb",
                borderRadius: 10,
                padding: "12px 14px",
                marginBottom: 20,
                fontSize: 14,
              }}
            >
              <strong>OGESTYLE support imported data for you</strong>
              <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
                {supportImports.map((entry) => (
                  <li key={entry.id}>
                    {entry.imported} {entry.dataType} record(s) on{" "}
                    {new Date(entry.createdAt).toLocaleString()}
                    {entry.performedByName ? ` by ${entry.performedByName}` : ""}
                    {entry.reason ? ` — ${entry.reason}` : ""}
                    {entry.errors ? ` (${entry.errors} row(s) could not be imported)` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Step Indicator */}
          <div
            className="step-indicator"
            style={{
              display: "flex",
              gap: "12px",
              marginBottom: "24px",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                padding: "8px 16px",
                borderRadius: "20px",
                backgroundColor: step >= 1 ? "#0ea5e9" : "#e5e7eb",
                color: step >= 1 ? "white" : "#666",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              1. Upload
            </div>
            <div
              style={{
                padding: "8px 16px",
                borderRadius: "20px",
                backgroundColor: step >= 2 ? "#0ea5e9" : "#e5e7eb",
                color: step >= 2 ? "white" : "#666",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              2. Map Columns
            </div>
            <div
              style={{
                padding: "8px 16px",
                borderRadius: "20px",
                backgroundColor: step >= 3 ? "#10b981" : "#e5e7eb",
                color: step >= 3 ? "white" : "#666",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              3. Complete
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div
              style={{
                background: "#fee2e2",
                border: "2px solid #ef4444",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "16px",
                color: "#991b1b",
              }}
            >
              <strong>⚠️ Error:</strong> {error}
            </div>
          )}

          {/* Migration Prerequisites Check */}
          {loadingReadiness && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div className="spinner-border" role="status">
                <span className="sr-only">Checking prerequisites...</span>
              </div>
              <p style={{ marginTop: "16px", color: "#666" }}>
                Checking migration prerequisites...
              </p>
            </div>
          )}

          {!loadingReadiness && readiness && !readiness.ready && (
            <div
              style={{
                background: "#fef3c7",
                border: "2px solid #f59e0b",
                borderRadius: "12px",
                padding: "24px",
                marginBottom: "32px",
              }}
            >
              <h2
                style={{
                  color: "#b45309",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ fontSize: "24px" }}>🚫</span>
                Appointments Import Blocked
              </h2>
              <p
                style={{
                  marginBottom: "12px",
                  color: "#78350f",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                You cannot import appointments yet.
              </p>
              {Array.isArray(readiness.blockingReasons) &&
                readiness.blockingReasons.length > 0 && (
                  <div
                    style={{
                      marginBottom: "16px",
                      padding: "12px 14px",
                      background: "#fff7ed",
                      border: "1px solid #fdba74",
                      borderRadius: "8px",
                      color: "#7c2d12",
                      fontSize: "14px",
                      lineHeight: "1.5",
                    }}
                  >
                    <strong>What you need to fix:</strong>
                    <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
                      {readiness.blockingReasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              <p
                style={{
                  marginBottom: "20px",
                  color: "#78350f",
                  lineHeight: "1.6",
                }}
              >
                <strong>Why?</strong> Each appointment needs to link to existing
                services and staff members. Without these, your imported
                appointments will be incomplete and won't show up correctly in
                your system.
              </p>
              <p
                style={{
                  marginBottom: "20px",
                  color: "#78350f",
                  fontSize: "15px",
                  fontWeight: "600",
                }}
              >
                📋 Complete these steps first (in this order):
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {/* Services Check */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px",
                    background: readiness.services > 0 ? "#d1fae5" : "#fee2e2",
                    borderRadius: "8px",
                    border: `2px solid ${readiness.services > 0 ? "#10b981" : "#ef4444"}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span style={{ fontSize: "24px" }}>
                      {readiness.services > 0 ? "✅" : "1️⃣"}
                    </span>
                    <div>
                      <strong
                        style={{
                          color: readiness.services > 0 ? "#065f46" : "#991b1b",
                        }}
                      >
                        {readiness.services === 0
                          ? "STEP 1: Import Your Services First"
                          : `Services Imported (${readiness.services} found)`}
                      </strong>
                      {readiness.services === 0 && (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontSize: "14px",
                            color: "#991b1b",
                          }}
                        >
                          Upload your services CSV before importing
                          appointments. Each appointment must link to a service.
                        </p>
                      )}
                    </div>
                  </div>
                  {readiness.services === 0 && (
                    <button
                      onClick={() => navigate("/dashboard/services")}
                      style={{
                        padding: "8px 16px",
                        background: "#0ea5e9",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        fontWeight: "600",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Import Services →
                    </button>
                  )}
                </div>

                {/* Staff Check */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px",
                    background: readiness.staff > 0 ? "#d1fae5" : "#fee2e2",
                    borderRadius: "8px",
                    border: `2px solid ${readiness.staff > 0 ? "#10b981" : "#ef4444"}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span style={{ fontSize: "24px" }}>
                      {readiness.staff > 0 ? "✅" : "2️⃣"}
                    </span>
                    <div>
                      <strong
                        style={{
                          color: readiness.staff > 0 ? "#065f46" : "#991b1b",
                        }}
                      >
                        {readiness.staff === 0
                          ? "STEP 2: Add Your Team Members"
                          : `Team Members Added (${readiness.staff} found)`}
                      </strong>
                      {readiness.staff === 0 && (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontSize: "14px",
                            color: "#991b1b",
                          }}
                        >
                          No active professionals found for this shop. Add or
                          activate at least one professional before importing
                          appointments.
                        </p>
                      )}
                    </div>
                  </div>
                  {readiness.staff === 0 && (
                    <button
                      onClick={() => navigate("/dashboard/team")}
                      style={{
                        padding: "8px 16px",
                        background: "#0ea5e9",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        fontWeight: "600",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Add Team →
                    </button>
                  )}
                </div>

                {/* Customers (always ready) */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px",
                    background: "#d1fae5",
                    borderRadius: "8px",
                    border: "2px solid #10b981",
                  }}
                >
                  <span style={{ fontSize: "24px", marginRight: "12px" }}>
                    3️⃣
                  </span>
                  <div>
                    <strong style={{ color: "#065f46" }}>
                      STEP 3: Import Appointments (After Steps 1 & 2)
                    </strong>
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "14px",
                        color: "#065f46",
                      }}
                    >
                      Customer accounts will be automatically created when you
                      import appointments. ✓ Ready to go!
                    </p>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "20px",
                  padding: "16px",
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  borderRadius: "8px",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    color: "#78350f",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  <strong>🔒 Why is this blocked?</strong>
                  <br />
                  Appointments must reference existing services and staff in
                  your system. Think of it like this:
                </p>
                <ul
                  style={{
                    margin: "8px 0 0 20px",
                    color: "#78350f",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  <li>
                    <strong>Services</strong> define what was done (e.g.,
                    "Haircut", "Massage")
                  </li>
                  <li>
                    <strong>Staff</strong> define who performed the service
                  </li>
                  <li>
                    <strong>Appointments</strong> connect customers to services
                    and staff
                  </li>
                </ul>
                <p
                  style={{
                    margin: "8px 0 0 0",
                    color: "#78350f",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  Without the first two, we can't properly import your
                  appointment history.
                </p>
              </div>

              <button
                onClick={checkMigrationReadiness}
                style={{
                  marginTop: "20px",
                  width: "100%",
                  padding: "12px",
                  background: "#0ea5e9",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "600",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              >
                🔄 I've Completed the Steps - Check Again
              </button>
            </div>
          )}

          {/* Only show import UI if prerequisites are met */}
          {!loadingReadiness && readiness && readiness.ready && (
            <>
              {/* STEP 1: Upload */}
              {step === 1 && (
                <>
                  {/* Instructions */}
                  <div className="instructions-section">
                    <h2>📋 How to Import</h2>
                    <ol>
                      <li>
                        <strong>Export from your current platform:</strong>{" "}
                        Squire, Booksy, Fresha, Vagaro, etc.
                      </li>
                      <li>
                        <strong>Upload the CSV file</strong> - we'll detect the
                        columns automatically
                      </li>
                      <li>
                        <strong>Confirm the mapping</strong> - make sure columns
                        match correctly
                      </li>
                      <li>
                        <strong>Import!</strong> - bookings will appear in your
                        calendar
                      </li>
                    </ol>

                    <div
                      style={{
                        backgroundColor: "#fef3c7",
                        border: "1px solid #fbbf24",
                        padding: "16px",
                        borderRadius: "8px",
                        marginTop: "16px",
                      }}
                    >
                      <strong>⚠️ Important:</strong> Import order matters. First
                      import your <strong>services</strong>, then{" "}
                      <strong>customers</strong>, then <strong>staff</strong>.
                      After that, import <strong>appointments here</strong>{" "}
                      (from the Appointments page).
                    </div>

                    <button
                      className="template-btn"
                      onClick={downloadTemplate}
                      style={{ marginTop: "16px" }}
                    >
                      📥 Download Example CSV
                    </button>
                  </div>

                  {/* File Upload */}
                  <div className="upload-section">
                    <h2>Upload CSV File</h2>
                    <div className="file-input-wrapper">
                      <input
                        id="file-input"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        disabled={importing}
                      />
                      {file && (
                        <div className="file-selected">
                          ✓ Selected: {file.name}
                        </div>
                      )}
                    </div>

                    {error && <div className="error-message">❌ {error}</div>}

                    <button
                      className="import-btn"
                      onClick={handlePreview}
                      disabled={!file || importing}
                    >
                      {importing
                        ? "Analyzing..."
                        : "Next: Preview & Map Columns"}
                    </button>
                  </div>
                </>
              )}

              {/* STEP 2: Preview & Column Mapping */}
              {step === 2 && previewData && (
                <>
                  <div
                    className="mapping-section"
                    style={{
                      backgroundColor: "white",
                      padding: "24px",
                      borderRadius: "12px",
                      marginBottom: "24px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <h2>📊 Column Mapping</h2>
                    <p style={{ color: "#666", marginBottom: "20px" }}>
                      We detected {previewData.headers.length} columns in your
                      CSV. Please confirm the mapping below:
                    </p>

                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        marginBottom: "12px",
                        alignItems: "center",
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleSaveMapping}
                        disabled={!Object.keys(columnMapping || {}).length}
                        style={{
                          padding: "8px 12px",
                          backgroundColor: Object.keys(columnMapping || {})
                            .length
                            ? "#0ea5e9"
                            : "#9ca3af",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: Object.keys(columnMapping || {}).length
                            ? "pointer"
                            : "not-allowed",
                          fontWeight: "600",
                        }}
                      >
                        💾 Save Mapping
                      </button>
                      {savedMapping && (
                        <>
                          <span style={{ fontSize: "12px", color: "#374151" }}>
                            Saved mapping loaded.
                          </span>
                          <button
                            type="button"
                            onClick={handleClearSavedMapping}
                            style={{
                              padding: "8px 12px",
                              backgroundColor: "#f87171",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontWeight: "600",
                            }}
                          >
                            Clear Saved
                          </button>
                        </>
                      )}
                    </div>

                    <div style={{ display: "grid", gap: "16px" }}>
                      {Object.entries(fieldDefinitions).map(([field, def]) => (
                        <div
                          key={field}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "200px 1fr",
                            gap: "12px",
                            alignItems: "center",
                            padding: "12px",
                            backgroundColor: "#f9fafb",
                            borderRadius: "8px",
                            border:
                              field.includes("*") ||
                              [
                                "staffName",
                                "customerName",
                                "customerEmail",
                                "appointmentDate",
                                "startTime",
                              ].includes(field)
                                ? "2px solid #0ea5e9"
                                : "1px solid #e5e7eb",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: "600",
                                fontSize: "14px",
                                marginBottom: "4px",
                              }}
                            >
                              {def.label}
                            </div>
                            <div style={{ fontSize: "11px", color: "#666" }}>
                              {def.description}
                            </div>
                          </div>
                          <select
                            value={columnMapping[field] || ""}
                            onChange={(e) =>
                              handleMappingChange(field, e.target.value)
                            }
                            style={{
                              padding: "8px 12px",
                              fontSize: "14px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              backgroundColor: "white",
                            }}
                          >
                            <option value="">-- Not mapped --</option>
                            <option
                              value="__CALCULATE__"
                              disabled={field !== "endTime"}
                            >
                              {field === "endTime"
                                ? "✓ Calculate from Start + Duration"
                                : "(Calculate)"}
                            </option>
                            {previewData.headers.map((header) => (
                              <option key={header} value={header}>
                                {header}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>

                    {error && (
                      <div
                        className="error-message"
                        style={{ marginTop: "16px" }}
                      >
                        ❌ {error}
                      </div>
                    )}
                  </div>

                  {/* Preview Table */}
                  <div
                    className="preview-section"
                    style={{
                      backgroundColor: "white",
                      padding: "24px",
                      borderRadius: "12px",
                      marginBottom: "24px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <h2>👁️ Preview (First 5 Rows)</h2>
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "13px",
                        }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: "#f3f4f6" }}>
                            {previewData.headers.map((header, idx) => (
                              <th
                                key={idx}
                                style={{
                                  padding: "10px",
                                  textAlign: "left",
                                  borderBottom: "2px solid #e5e7eb",
                                  fontWeight: "600",
                                }}
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.preview.map((row, rowIdx) => (
                            <tr
                              key={rowIdx}
                              style={{
                                borderBottom: "1px solid #e5e7eb",
                              }}
                            >
                              {previewData.headers.map((header, colIdx) => (
                                <td
                                  key={colIdx}
                                  style={{
                                    padding: "10px",
                                    color: "#374151",
                                  }}
                                >
                                  {row[header] || (
                                    <span style={{ color: "#9ca3af" }}>--</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Actions */}
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      onClick={handleStartOver}
                      disabled={importing}
                      style={{
                        padding: "12px 24px",
                        fontSize: "16px",
                        fontWeight: "600",
                        backgroundColor: "#f3f4f6",
                        color: "#374151",
                        border: "none",
                        borderRadius: "8px",
                        cursor: importing ? "not-allowed" : "pointer",
                      }}
                    >
                      ← Back
                    </button>
                    <button
                      onClick={handleImport}
                      disabled={
                        importing ||
                        !columnMapping.staffName ||
                        !columnMapping.customerName ||
                        !columnMapping.customerEmail
                      }
                      style={{
                        padding: "12px 32px",
                        fontSize: "16px",
                        fontWeight: "600",
                        backgroundColor:
                          !columnMapping.staffName ||
                          !columnMapping.customerName ||
                          !columnMapping.customerEmail
                            ? "#9ca3af"
                            : "#10b981",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor:
                          importing ||
                          !columnMapping.staffName ||
                          !columnMapping.customerName ||
                          !columnMapping.customerEmail
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      {importing ? "⏳ Importing..." : "✓ Import Bookings"}
                    </button>
                  </div>
                </>
              )}

              {/* STEP 3: Results */}
              {step === 3 && results && (
                <>
                  <div
                    className="results-section"
                    style={{
                      backgroundColor: "white",
                      padding: "24px",
                      borderRadius: "12px",
                      marginBottom: "24px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <h2>✅ Import Complete!</h2>
                    <div className="results-summary">
                      <div className="result-stat success">
                        <span className="label">✓ Successfully Imported:</span>
                        <span className="value">{results.imported}</span>
                      </div>
                      {results.needsAssignment > 0 && (
                        <div className="result-stat warning">
                          <span className="label">
                            ⚠️ Need Staff Assignment:
                          </span>
                          <span className="value">
                            {results.needsAssignment}
                          </span>
                        </div>
                      )}
                      {results.failed > 0 && (
                        <div className="result-stat error">
                          <span className="label">✗ Failed:</span>
                          <span className="value">{results.failed}</span>
                        </div>
                      )}
                      {results.skipped > 0 && (
                        <div className="result-stat warning">
                          <span className="label">⤼ Skipped (duplicates):</span>
                          <span className="value">{results.skipped}</span>
                        </div>
                      )}
                    </div>

                    {results.errors && results.errors.length > 0 && (
                      <div
                        className="errors-list"
                        style={{ marginTop: "20px" }}
                      >
                        <h3>Details:</h3>
                        {results.errors.map((err, idx) => (
                          <div
                            key={idx}
                            className="error-item"
                            style={{
                              padding: "8px 12px",
                              backgroundColor: err.warning
                                ? "#fef3c7"
                                : "#fee2e2",
                              borderRadius: "6px",
                              marginBottom: "8px",
                              fontSize: "13px",
                            }}
                          >
                            <strong>Row {err.row}:</strong> {err.error}
                          </div>
                        ))}
                      </div>
                    )}

                    {results.needsAssignment > 0 && (
                      <div
                        style={{
                          marginTop: "20px",
                          padding: "16px",
                          backgroundColor: "#fef3c7",
                          border: "2px solid #fbbf24",
                          borderRadius: "8px",
                        }}
                      >
                        <strong>⚠️ Action Required:</strong>
                        <p style={{ margin: "8px 0 0 0" }}>
                          {results.needsAssignment} booking
                          {results.needsAssignment > 1 ? "s" : ""} need
                          {results.needsAssignment === 1 ? "s" : ""} staff
                          assignment. Go to the <strong>Bookings</strong> page
                          to assign them to the correct professionals.
                        </p>
                      </div>
                    )}

                    <p
                      className="success-note"
                      style={{
                        marginTop: "20px",
                        color: "#10b981",
                        fontWeight: "500",
                      }}
                    >
                      ✓ Imported bookings are now visible in your Bookings page.
                    </p>
                  </div>

                  {/* Actions */}
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      onClick={handleStartOver}
                      style={{
                        padding: "12px 24px",
                        fontSize: "16px",
                        fontWeight: "600",
                        backgroundColor: "#f3f4f6",
                        color: "#374151",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      Import Another File
                    </button>
                    <button
                      onClick={() => navigate("/dashboard/bookings")}
                      style={{
                        padding: "12px 32px",
                        fontSize: "16px",
                        fontWeight: "600",
                        backgroundColor: "#0ea5e9",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      View Bookings →
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* Help Section */}
          <div
            className="sync-info-section"
            style={{
              marginTop: "32px",
              padding: "20px",
              backgroundColor: "#f9fafb",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
            }}
          >
            <h3>💡 Tips for Successful Import</h3>
            <ul
              style={{ marginTop: "12px", lineHeight: "1.8", color: "#374151" }}
            >
              <li>
                <strong>Staff names must match exactly</strong> - Import your
                staff first using the Staff page
              </li>
              <li>
                <strong>Service names are optional</strong> - If not matched,
                bookings import without service link
              </li>
              <li>
                <strong>Dates must be valid</strong> - Use YYYY-MM-DD format
                (e.g., 2026-01-15)
              </li>
              <li>
                <strong>Times use 24-hour format</strong> - 14:30 for 2:30 PM
              </li>
              <li>
                <strong>Duplicate bookings are skipped</strong> - Same staff,
                customer, date, and time
              </li>
            </ul>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
}

export default BookingImport;
